import dayjs from 'dayjs';
import type { TFunction } from 'i18next';

import { TravelRegistryFilter, TravelRegistryLang } from '@/module/Travel/Registry/model/TravelRegistryTypes';
import { TravelEnforcement } from '@/module/Travel/Settings/model/TravelSettingsTypes';

import { Nomenclature } from '@feature/nomenclature';
import { RoleType } from '@feature/security';
import {
  Currency,
  DAYJS_SERVER_DATE_FORMAT,
  Id,
  IdentityRequest,
  NamedEntity,
  PagedParams,
  PagedResponse,
  PersistentFile,
  formatDate,
} from '@feature/utils';

export const travelActivityTypeList = [
  'SCIENTIFIC_EVENT',
  'SCHOLARSHIP',
  'PROFESSIONAL_DEVELOPMENT',
  'EXPERIENCE_EXCHANGE',
  'ERASMUS',
] as const;

export type TravelActivityType = (typeof travelActivityTypeList)[number];

export const travelRegistryTypeList = ['RESEARCH', 'ERASMUS'] as const;

export type TravelRegistryType = (typeof travelRegistryTypeList)[number];

// A scientific event names its organiser where every other type names the institution travelled to.
export function travelDestinationInstitutionField(activityType: TravelActivityType) {
  return activityType === 'SCIENTIFIC_EVENT' ? 'eventOrganiser' : 'destinationInstitution';
}

// Mirrors the server's split: Erasmus trips are numbered in their own registry, every other type in the research one.
export function getTravelRegistryType(activityType: TravelActivityType): TravelRegistryType {
  return activityType === 'ERASMUS' ? 'ERASMUS' : 'RESEARCH';
}

export const erasmusTypeList = ['TEACHING', 'TRAINING'] as const;

export type ErasmusType = (typeof erasmusTypeList)[number];

export const travelRequestStatusList = [
  'DRAFT',
  'SUBMITTED',
  'TRAVEL_ORDER_ISSUED',
  'APPROVED',
  'REJECTED',
  'RETURNED_FOR_CHANGES',
  'COMPLETED',
  'CANCELLED',
] as const;

export type TravelRequestStatus = (typeof travelRequestStatusList)[number];

export const travelFileKindList = [
  'PROGRAM_OR_INVITATION',
  'SCHOLARSHIP_AWARD_PROOF',
  'MOBILITY_APPLICATION',
  'HOST_INVITATION',
  'HOST_ACTIVITY_PROGRAM',
  'PARTICIPATION_CERTIFICATE',
  'ACTIVITY_REPORT',
  'TRAVEL_ORDER_WITH_RECEIPTS',
  'SHORT_REPORT',
  'MOBILITY_CERTIFICATE',
  'TEACHING_COVERAGE_PROOF',
  'HR_LEAVE_MEMO',
  'UNPAID_LEAVE_REQUEST',
  'ADMINISTRATIVE_BOARD_DECISION',
  'OTHER',
] as const;

export type TravelFileKind = (typeof travelFileKindList)[number];

export const travelReturnFileKindList = [
  'PARTICIPATION_CERTIFICATE',
  'ACTIVITY_REPORT',
  'TRAVEL_ORDER_WITH_RECEIPTS',
  'SHORT_REPORT',
  'MOBILITY_CERTIFICATE',
] as const satisfies readonly TravelFileKind[];

export type TravelReturnFileKind = (typeof travelReturnFileKindList)[number];

export const travelReturnUploadKindList = [
  ...travelReturnFileKindList,
  'OTHER',
] as const satisfies readonly TravelFileKind[];

export const travelOfficeFileKindList = ['ADMINISTRATIVE_BOARD_DECISION'] as const satisfies readonly TravelFileKind[];

export type TravelOfficeFileKind = (typeof travelOfficeFileKindList)[number];

export const travelSubmissionFileKindList = travelFileKindList.filter(
  (kind): kind is Exclude<TravelFileKind, TravelReturnFileKind | TravelOfficeFileKind> =>
    !(travelReturnFileKindList as readonly TravelFileKind[]).includes(kind) &&
    !(travelOfficeFileKindList as readonly TravelFileKind[]).includes(kind),
);

export function isTravelReturnFileKind(kind: TravelFileKind): kind is TravelReturnFileKind {
  return (travelReturnFileKindList as readonly TravelFileKind[]).includes(kind);
}

export const teachingCoverageModeList = ['RECOVERY', 'COLLEAGUE_SUBSTITUTION', 'NO_TEACHING_ACTIVITIES'] as const;

export type TeachingCoverageMode = (typeof teachingCoverageModeList)[number];

export interface TeachingCoverageProof {
  kind: TravelFileKind;
  requiredAtSubmit: boolean;
}

// A coverage mode listed here owes a proof file of its own kind.
export const teachingCoverageProofs: Partial<Record<TeachingCoverageMode, TeachingCoverageProof>> = {
  RECOVERY: { kind: 'TEACHING_COVERAGE_PROOF', requiredAtSubmit: true },
};

export const absenceMotivationList = [
  'WITH_PAPER_WITHIN_CAP',
  'PROFESSIONAL_DEVELOPMENT_WITHIN_5',
  'BEYOND_CAP_ON_LEAVE',
  'OWN_PROGRAMS',
] as const;

export type AbsenceMotivation = (typeof absenceMotivationList)[number];

// Procedure UMFST-PO-CC-13 numbers the motivations with these letters, and the F01 form prints them too.
const absenceMotivationLetters: Record<AbsenceMotivation, string> = {
  WITH_PAPER_WITHIN_CAP: 'A',
  PROFESSIONAL_DEVELOPMENT_WITHIN_5: 'B',
  BEYOND_CAP_ON_LEAVE: 'C',
  OWN_PROGRAMS: 'D',
};

export function absenceMotivationLabel(motivation: AbsenceMotivation, t: TFunction) {
  const label = t(`travel.absenceMotivationEnum.${motivation}`);
  const letter = absenceMotivationLetters[motivation];
  return letter ? `${letter}. ${label}` : label;
}

// UMFST mobility support pays the trip itself, so it never combines with a motivation paid from the yearly cap.
export const mobilitySupportExcludedMotivations: readonly AbsenceMotivation[] = [
  'WITH_PAPER_WITHIN_CAP',
  'PROFESSIONAL_DEVELOPMENT_WITHIN_5',
];

// Own programs are the one motivation that cannot be combined with rest or unpaid leave days.
export function absenceMotivationTakesLeave(motivation: AbsenceMotivation | null | undefined): boolean {
  return motivation != null && motivation !== 'OWN_PROGRAMS';
}

export const travelExpenseTypeList = [
  'TRANSPORT',
  'ACCOMMODATION',
  'PARTICIPATION_FEE',
  'UMFST_MOBILITY_SUPPORT',
] as const;

export type TravelExpenseType = (typeof travelExpenseTypeList)[number];

export const travelFundingSourceList = [
  'ORGANIZERS',
  'PARTICIPANT',
  'SPONSORS',
  'UMFST',
  'ERASMUS_FUNDS',
  'PROJECT_FUNDS',
  'UMFST_OWN_REVENUE',
] as const;

export type TravelFundingSource = (typeof travelFundingSourceList)[number];

// Kept in chain order, which the approval timeline reads to tell one round from the next.
export const travelStepKeyList = [
  'HEAD',
  'DEAN',
  'SECRETARIAT_VERIFICATION',
  'RESEARCH_VICE_RECTOR',
  'INTERNATIONAL_VICE_RECTOR',
  'ADMINISTRATIVE_BOARD',
] as const;

export type TravelStepKey = (typeof travelStepKeyList)[number];

export type TravelStepFamily = 'endorsement' | 'verification' | 'approval';

const travelStepFamilyByKey: Record<TravelStepKey, TravelStepFamily> = {
  HEAD: 'endorsement',
  DEAN: 'endorsement',
  SECRETARIAT_VERIFICATION: 'verification',
  RESEARCH_VICE_RECTOR: 'endorsement',
  INTERNATIONAL_VICE_RECTOR: 'approval',
  ADMINISTRATIVE_BOARD: 'approval',
};

export function travelStepFamily(step: Pick<TravelApprovalStep, 'stepKey'>): TravelStepFamily {
  return travelStepFamilyByKey[step.stepKey];
}

export const travelStepDecisionList = ['PENDING', 'APPROVED', 'RETURNED', 'REJECTED', 'SKIPPED'] as const;

export type TravelStepDecision = (typeof travelStepDecisionList)[number];

export type Country = Nomenclature<{ isoCode: string }>;

export interface TravelFile extends PersistentFile {
  path: string;
}

export interface TravelRequestRegistration {
  registrationNumber: number;
  registrationYear: number;
  registrationDate: string | null;
  registryType: TravelRegistryType;
}

export function formatTravelRegistration({
  registrationNumber,
  registrationYear,
  registrationDate,
}: Omit<TravelRequestRegistration, 'registryType'>) {
  return `${registrationNumber}/${formatDate(registrationDate) ?? registrationYear}`;
}

// For sentences that name a request by its number alone, where the two registries would read the same.
export function formatTravelRegistrationWithType(
  registration: Omit<TravelRequestRegistration, 'registryType'> & { registryType: TravelRegistryType | null },
  t: TFunction,
) {
  const label = formatTravelRegistration(registration);
  return registration.registryType == null
    ? label
    : `${label} (${t(`travel.registryTypeEnum.${registration.registryType}`)})`;
}

export interface TravelRequestAffiliation {
  facultyName: string | null;
  departmentName: string | null;
  disciplineName: string | null;
  jobTitle: string | null;
}

export interface TravelRequestAbsence {
  teachingCoverageMode: TeachingCoverageMode | null;
  teachingCoverageNote: string | null;
  absenceMotivation: AbsenceMotivation | null;
  vacationDays: number | null;
  unpaidDays: number | null;
  ownProgramsMention: string | null;
  substitutePersonName: string | null;
}

export interface TravelRequestFunding {
  perDiem: boolean;
  fundingSourceNote: string | null;
  estimatedAmount: number | null;
  estimatedAmountCurrency: Currency | null;
}

export interface TravelExpenseRequest {
  expenseType: TravelExpenseType;
  fundingSource: TravelFundingSource;
  requestedAmount: number | null;
  currency: Currency;
  eurAmount: number | null;
  projectNumber: string | null;
  projectTitle: string | null;
  projectAmount: number | null;
  projectEurAmount: number | null;
}

export interface TravelExpense extends TravelExpenseRequest {
  id: number;
}

// Two lines of one request may not share this key, and a project line also counts its project number.
export function travelExpenseKey(
  expense: Pick<TravelExpenseRequest, 'expenseType' | 'fundingSource' | 'projectNumber'>,
) {
  const project = expense.fundingSource === 'PROJECT_FUNDS' ? (expense.projectNumber ?? '').trim().toLowerCase() : '';
  return `${expense.expenseType}:${expense.fundingSource}:${project}`;
}

export interface TravelRequestFile {
  id: number;
  kind: TravelFileKind;
  file: TravelFile;
  uploadedBy: NamedEntity;
  uploadedAt: string;
}

export interface TravelApprovalStep {
  id: number;
  stepKey: TravelStepKey;
  orderIndex: number;
  approver: NamedEntity | null;
  roleType: RoleType | null;
  // Server-written title that replaces the default one, already in its display language.
  stepLabel?: string | null;
  decision: TravelStepDecision;
  // Set on the returned step whose resubmission sent the request back to the first approver.
  restartedAfterChanges?: boolean;
  reason: string | null;
  decidedAt: string | null;
  activatedAt: string | null;
  remindersSent: number;
  lastReminderAt: string | null;
  // Set only on the board step, once the office marks the request as sent to the board.
  sentToBoardAt?: string | null;
  sentToBoardByName?: string | null;
}

export interface TravelRequestSummary {
  id: number;
  requester: NamedEntity;
  activityType: TravelActivityType;
  name: string | null;
  city: string | null;
  country: Country | null;
  departureDate: string | null;
  returnDate: string | null;
  eventStartDate: string | null;
  eventEndDate: string | null;
  status: TravelRequestStatus;
  // The first pending step, which tells a research office verification row from a board row.
  currentStepKey: TravelStepKey | null;
  lateSubmission: boolean;
  // Server-computed: overlapping unfinished trips university-wide with this one excluded, and the requester's approved trips already over and never completed.
  parallelRequests: number;
  unfinishedReturns: number;
  registration: TravelRequestRegistration | null;
  leaveDaysExceeded: boolean;
  dayCapExceeded: boolean;
  dayBalance: TravelDayBalanceSummary | null;
}

export interface TravelDayBalanceTrip {
  id: number;
  name: string | null;
  registration: string | null;
  registrationNumber: number | null;
  registrationYear: number | null;
  registrationDate: string | null;
  registryType: TravelRegistryType | null;
  absenceMotivation: AbsenceMotivation | null;
  status: TravelRequestStatus;
  paidDays: number;
  professionalDevelopmentDays: number;
  vacationDays: number;
  unpaidDays: number;
}

export interface TravelDayBalanceYear {
  year: number;
  withPaperUsed: number;
  withPaperCap: number;
  professionalDevelopmentUsed: number;
  professionalDevelopmentCap: number;
  thisRequestPaidDays: number;
  thisRequestProfessionalDevelopmentDays: number;
  withPaperAfter: number;
  vacationDays: number;
  unpaidDays: number;
  thisRequestVacationDays: number;
  thisRequestUnpaidDays: number;
  trips: TravelDayBalanceTrip[];
}

// Used days exclude the request itself, and the backend sends null for a draft.
export interface TravelDayBalance {
  consumes: boolean;
  years: TravelDayBalanceYear[];
}

export interface TravelDayBalanceSummaryYear {
  year: number;
  withPaperUsed: number;
  thisRequestPaidDays: number;
  withPaperCap: number;
  exceeded: boolean;
  vacationDays: number;
  unpaidDays: number;
  thisRequestVacationDays: number;
  thisRequestUnpaidDays: number;
}

export interface TravelDayBalanceSummary {
  consumes: boolean;
  years: TravelDayBalanceSummaryYear[];
}

export interface TravelOwnDayBalance {
  year: number;
  withPaperUsed: number;
  withPaperCap: number;
  professionalDevelopmentUsed: number;
  professionalDevelopmentCap: number;
  vacationDays: number;
  unpaidDays: number;
  trips: TravelDayBalanceTrip[];
}

export interface TravelDayBalancePerson {
  userId: number;
  fullName: string;
  facultyName: string | null;
  departmentName: string | null;
  withPaperUsed: number;
  withPaperCap: number;
  professionalDevelopmentUsed: number;
  professionalDevelopmentCap: number;
  vacationDays: number;
  unpaidDays: number;
}

export interface TravelDayBalancePeopleFilter {
  year: number;
  facultyId?: Id | null;
}

export type TravelDayBalancePeopleParams = PagedParams<TravelDayBalancePeopleFilter>;

export interface TravelDayBalancePeopleExportRequest extends TravelDayBalancePeopleFilter {
  lang: TravelRegistryLang;
}

export type TravelDayBalancePeoplePage = PagedResponse<TravelDayBalancePerson>;

export const TRAVEL_DAY_BALANCE_PEOPLE_PAGE_SIZE = 20;

export interface TravelDayBalancePreviewParams {
  departureDate: string;
  returnDate: string;
  activityType?: TravelActivityType | null;
  presentsPaper?: boolean | null;
  absenceMotivation?: AbsenceMotivation | null;
  vacationDays?: number | null;
  unpaidDays?: number | null;
  requestId?: number | null;
}

export interface TravelDayBalancePreviewYear {
  year: number;
  withPaperUsed: number;
  withPaperCap: number;
  professionalDevelopmentUsed: number;
  professionalDevelopmentCap: number;
  thisRequestPaidDays: number;
  thisRequestProfessionalDevelopmentDays: number;
  withPaperAfter: number;
  exceedsWithPaper: boolean;
  exceedsProfessionalDevelopment: boolean;
}

export interface TravelDayBalancePreview {
  calendarDays: number;
  workingDays: number;
  legalHolidays: string[];
  consumes: boolean;
  allowedMotivations: AbsenceMotivation[];
  leaveDays: number;
  leaveDaysExceedWorkingDays: boolean;
  minimumLeaveDays: number;
  // The most working days of this trip the yearly caps can still pay, whatever leave it takes; null where no cap applies.
  capUsableDays?: number | null;
  years: TravelDayBalancePreviewYear[];
  enforcement: { leaveDays: TravelEnforcement; dayCap: TravelEnforcement };
}

export type TravelDayTotals = Pick<
  TravelDayBalancePreview,
  'workingDays' | 'capUsableDays' | 'allowedMotivations' | 'enforcement'
>;

export interface TravelCancellation {
  cancelledAt: string;
  cancelledBy: NamedEntity;
  reason: string;
}

export interface TravelCancelRequest {
  reason: string;
}

export interface TravelOverlapRequest {
  id: number;
  registrationNumber: number | null;
  registrationYear: number | null;
  registrationDate: string | null;
  registryType: TravelRegistryType | null;
  requester: NamedEntity;
  name: string | null;
  paperTitle: string | null;
  destinationInstitution: string | null;
  departureDate: string | null;
  returnDate: string | null;
  status: TravelRequestStatus;
}

export interface TravelOverlap {
  kind: 'SAME_PAPER';
  exact: boolean;
  // Between 0.85 and 1.
  similarity: number;
  request: TravelOverlapRequest;
}

export interface TravelPaperTitleMatch {
  exact: boolean;
  similarity: number;
  paperTitle: string;
  requester: NamedEntity;
}

export interface TravelPaperTitleCheck {
  taken: boolean;
  matches: TravelPaperTitleMatch[];
}

export interface TravelPaperTitleCheckParams {
  paperTitle: string;
  // The request being edited, which the backend leaves out of the matching.
  requestId: number | null;
}

export type TravelRequestParams = PagedParams<TravelRegistryFilter>;

export const travelDocumentFormList = ['F01', 'F02'] as const;
export type TravelDocumentForm = (typeof travelDocumentFormList)[number];

export interface TravelDocument {
  available: boolean;
  stored: boolean;
  form: TravelDocumentForm;
  formCode: string;
  fileName: string | null;
  sizeInBytes: number | null;
}

export interface TravelOrder {
  available: boolean;
  fileName: string | null;
}

export interface TravelRequest {
  id: number;
  requester: NamedEntity;
  activityType: TravelActivityType;
  erasmusType: ErasmusType | null;
  name: string | null;
  destinationInstitution: string | null;
  city: string | null;
  country: Country | null;
  departureDate: string | null;
  returnDate: string | null;
  // The effective period of the event or mobility, inside the trip range; null only on a draft that never filled it.
  eventStartDate: string | null;
  eventEndDate: string | null;
  requestDate: string | null;
  presentsPaper: boolean | null;
  paperTitle: string | null;
  status: TravelRequestStatus;
  lateSubmission: boolean;
  parallelRequests: number;
  parallelEnforcement: TravelEnforcement;
  unfinishedReturns: number;
  // The requester's other Erasmus trips still missing the mobility certificate, empty unless this one is Erasmus.
  unfinishedErasmusRequests: TravelOverlapRequest[];
  // The requester's other trips already over and never completed, empty when this one is Erasmus.
  unfinishedReturnRequests: TravelOverlapRequest[];
  document: TravelDocument;
  travelOrder: TravelOrder;
  leaveDaysExceeded: boolean;
  dayCapExceeded: boolean;
  dayBalance: TravelDayBalance | null;
  canCancel: boolean;
  // False until the day after the return date, even for the requester of an approved request.
  canComplete: boolean;
  cancellation: TravelCancellation | null;

  affiliation: TravelRequestAffiliation | null;
  registration: TravelRequestRegistration | null;
  absence: TravelRequestAbsence | null;
  funding: TravelRequestFunding | null;

  expenses: TravelExpense[];
  files: TravelRequestFile[];
  approvalSteps: TravelApprovalStep[];
}

export type TravelRequestAbsenceRequest = TravelRequestAbsence;

export type TravelRequestFundingRequest = TravelRequestFunding;

export interface TravelRequestRequest {
  activityType: TravelActivityType;
  erasmusType: ErasmusType | null;
  name: string | null;
  destinationInstitution: string | null;
  city: string | null;
  country: IdentityRequest | null;
  departureDate: string | null;
  returnDate: string | null;
  eventStartDate: string | null;
  eventEndDate: string | null;
  presentsPaper: boolean | null;
  paperTitle: string | null;

  absence: TravelRequestAbsenceRequest;
  funding: TravelRequestFundingRequest;

  expenses: TravelExpenseRequest[];
}

export interface TravelFileUploadRequest {
  kind: TravelFileKind;
  file: File;
}

export interface TravelFileDownload {
  fileId: Id;
  fallbackFilename: string;
}

// The backend dropped `expected` with its supporting-documents phase and added the two guidance strings there.
export interface TravelDocumentKinds {
  required: TravelFileKind[];
  expected?: TravelFileKind[];
  returnRequired: TravelFileKind[];
  // Each group is settled by any one of its kinds; a backend older than the groups sends none.
  returnOneOf?: TravelFileKind[][];
  // What the requester may attach in each stage for the activity type; a backend older than the lists sends none.
  submission?: TravelFileKind[];
  returnUpload?: TravelFileKind[];
  approvalGuidance?: string | null;
  returnGuidance?: string | null;
}

export interface TravelRequestPermissions {
  canEdit: boolean;
  canSubmit: boolean;
  canDelete: boolean;
}

// The backend handles a requester's own request through the AUTHOR role only, so ownership alone grants nothing.
export function travelRequestPermissions(
  status: TravelRequestStatus,
  isRequester: boolean,
  currentRole: RoleType,
): TravelRequestPermissions {
  const requester = isRequester && currentRole === RoleType.AUTHOR;
  const changeable = requester && (status === 'DRAFT' || status === 'RETURNED_FOR_CHANGES');

  return {
    canEdit: changeable,
    canSubmit: changeable,
    canDelete: requester && status === 'DRAFT',
  };
}

// The server refuses completion for one reason only on the requester's own approved request: the trip is not over.
export function isTravelReturnDatePending(request: TravelRequest, isRequester: boolean, currentRole: RoleType) {
  return isRequester && currentRole === RoleType.AUTHOR && request.status === 'APPROVED' && !request.canComplete;
}

export function sortTravelApprovalSteps(steps: TravelApprovalStep[]) {
  return [...steps].sort((a, b) => a.orderIndex - b.orderIndex || a.id - b.id);
}

// The HEAD step lands on a discipline or a department head, so its own role names it better than the step key.
export function travelStepLabelKey(step: Pick<TravelApprovalStep, 'stepKey' | 'roleType'>) {
  if (
    step.stepKey === 'HEAD' &&
    (step.roleType === RoleType.DISCIPLINE_ADMIN || step.roleType === RoleType.DEPARTMENT_ADMIN)
  ) {
    return `roleType.${step.roleType}`;
  }
  // One step key serves both chains, so the office that holds the step names it.
  if (
    step.stepKey === 'SECRETARIAT_VERIFICATION' &&
    (step.roleType === RoleType.RESEARCH_ADMINISTRATIVE_OFFICE ||
      step.roleType === RoleType.INTERNATIONAL_ADMINISTRATIVE_OFFICE)
  ) {
    return `travel.secretariatVerificationStepEnum.${step.roleType}`;
  }

  return `travel.stepKeyEnum.${step.stepKey}`;
}

function travelStepLabelOverride(step: Pick<TravelApprovalStep, 'stepLabel'>) {
  return step.stepLabel?.trim() || null;
}

export function travelStepTitle(step: Pick<TravelApprovalStep, 'stepKey' | 'roleType' | 'stepLabel'>, t: TFunction) {
  return travelStepLabelOverride(step) ?? t(travelStepLabelKey(step));
}

// An overridden title names who holds the step, so it stands in for the role name too.
export function travelStepRoleLabel(step: Pick<TravelApprovalStep, 'roleType' | 'stepLabel'>, t: TFunction) {
  return travelStepLabelOverride(step) ?? (step.roleType == null ? null : t(`roleType.${step.roleType}`));
}

const travelPooledStepKeys: readonly TravelStepKey[] = ['ADMINISTRATIVE_BOARD', 'SECRETARIAT_VERIFICATION'];

// A pooled step names no approver until a seated member of its office decides it.
export function isTravelPooledStep(step: Pick<TravelApprovalStep, 'stepKey'>) {
  return travelPooledStepKeys.includes(step.stepKey);
}

export function currentTravelApprovalStep(steps: TravelApprovalStep[]) {
  return sortTravelApprovalSteps(steps).find(step => step.decision === 'PENDING') ?? null;
}

export function pendingTravelBoardStep(request: TravelRequest) {
  const step = currentTravelApprovalStep(request.approvalSteps);

  return step?.stepKey === 'ADMINISTRATIVE_BOARD' ? step : null;
}

// A rejection leaves the later steps pending, so an undecided board step counts only where the request stopped.
export function travelBoardReached(request: TravelRequest) {
  const steps = sortTravelApprovalSteps(request.approvalSteps);
  const board = steps.filter(step => step.stepKey === 'ADMINISTRATIVE_BOARD').pop();
  if (board == null) {
    return false;
  }
  if (board.decision !== 'PENDING') {
    return true;
  }

  return request.status !== 'REJECTED' && currentTravelApprovalStep(request.approvalSteps)?.id === board.id;
}

export function canManageTravelBoardDecisions(request: TravelRequest, currentRole: RoleType) {
  return (
    currentRole === RoleType.RESEARCH_ADMINISTRATIVE_OFFICE &&
    request.status === 'SUBMITTED' &&
    pendingTravelBoardStep(request) != null
  );
}

// Both times are zone-free server clock values, read as UTC so a DST gap cannot reorder them; without an activation time any decision counts.
export function currentTravelBoardDecisionFiles(request: TravelRequest) {
  const step = pendingTravelBoardStep(request);
  if (step == null) {
    return [];
  }

  return request.files.filter(
    file =>
      file.kind === 'ADMINISTRATIVE_BOARD_DECISION' &&
      (step.activatedAt == null || !dayjs.utc(file.uploadedAt).isBefore(dayjs.utc(step.activatedAt))),
  );
}

export function hasCurrentTravelBoardDecision(request: TravelRequest) {
  return currentTravelBoardDecisionFiles(request).length > 0;
}

const hrMemoStaffStatuses: TravelRequestStatus[] = ['SUBMITTED', 'TRAVEL_ORDER_ISSUED', 'APPROVED'];

// Erasmus+ mobilities carry no HR memo, so only the research office ever attaches or removes one.
export function canManageTravelHrMemo(request: TravelRequest, isRequester: boolean, currentRole: RoleType) {
  return (
    !isRequester &&
    currentRole === RoleType.RESEARCH_ADMINISTRATIVE_OFFICE &&
    hrMemoStaffStatuses.includes(request.status) &&
    request.activityType !== 'ERASMUS'
  );
}

export function missingTravelReturnKinds(request: TravelRequest, documentKinds: TravelDocumentKinds) {
  const uploadedKinds = new Set(request.files.map(file => file.kind));

  return documentKinds.returnRequired.filter(kind => !uploadedKinds.has(kind));
}

export function travelReturnOneOfGroups(documentKinds: TravelDocumentKinds) {
  return (documentKinds.returnOneOf ?? []).filter(group => group.length > 0);
}

export function unsatisfiedTravelReturnGroups(request: TravelRequest, documentKinds: TravelDocumentKinds) {
  const uploadedKinds = new Set(request.files.map(file => file.kind));

  return travelReturnOneOfGroups(documentKinds).filter(group => !group.some(kind => uploadedKinds.has(kind)));
}

export function travelReturnDocumentsComplete(request: TravelRequest, documentKinds: TravelDocumentKinds) {
  return (
    missingTravelReturnKinds(request, documentKinds).length === 0 &&
    unsatisfiedTravelReturnGroups(request, documentKinds).length === 0
  );
}

// A professional development stage proves attendance, so its participation certificate slot reads as a presence certificate.
export function travelFileKindLabel(t: TFunction, kind: TravelFileKind, activityType?: TravelActivityType) {
  if (kind === 'PARTICIPATION_CERTIFICATE' && activityType === 'PROFESSIONAL_DEVELOPMENT') {
    return t('travel.fileKindPresenceCertificate');
  }

  return t(`travel.fileKindEnum.${kind}`);
}

export function travelReturnGroupLabel(t: TFunction, group: TravelFileKind[], activityType?: TravelActivityType) {
  const kinds = group
    .map(kind => travelFileKindLabel(t, kind, activityType))
    .join(` ${t('travel.requests.view.documentOneOfJoin')} `);

  return t('travel.requests.view.documentOneOf', { kinds });
}

export function hasTravelMobilitySupport(request: Pick<TravelRequest, 'expenses'>) {
  return request.expenses.some(expense => expense.expenseType === 'UMFST_MOBILITY_SUPPORT');
}

// The coverage proof and the unpaid leave request hang on the request's own content, so the per-activity-type document kinds never list them.
export function requiredTravelSubmissionKinds(request: TravelRequest, documentKinds: TravelDocumentKinds | undefined) {
  const kinds = new Set(documentKinds?.required ?? []);
  const coverageMode = request.absence?.teachingCoverageMode;
  const coverageProof = coverageMode == null ? undefined : teachingCoverageProofs[coverageMode];
  if (coverageProof?.requiredAtSubmit) kinds.add(coverageProof.kind);
  if ((request.absence?.unpaidDays ?? 0) > 0) kinds.add('UNPAID_LEAVE_REQUEST');

  return [...kinds];
}

// Narrows what the activity type accepts to the kinds this request can use right now.
export function travelSubmissionUploadKinds(
  request: TravelRequest,
  documentKinds: TravelDocumentKinds | undefined,
): TravelFileKind[] {
  const offered: readonly TravelFileKind[] = documentKinds?.submission ?? travelSubmissionFileKindList;
  const coverageMode = request.absence?.teachingCoverageMode;
  const withCoverageProof = coverageMode != null && teachingCoverageProofs[coverageMode] != null;
  const withUnpaidLeave = (request.absence?.unpaidDays ?? 0) > 0;

  return offered.filter(
    kind =>
      kind !== 'HR_LEAVE_MEMO' &&
      (kind !== 'TEACHING_COVERAGE_PROOF' || withCoverageProof) &&
      (kind !== 'UNPAID_LEAVE_REQUEST' || withUnpaidLeave),
  );
}

// Until the trip is over the server takes only an "other" file from the requester in the return stage.
export function travelReturnUploadKinds(
  documentKinds: TravelDocumentKinds | undefined,
  returnDatePending: boolean,
): TravelFileKind[] {
  const offered: readonly TravelFileKind[] = documentKinds?.returnUpload ?? travelReturnUploadKindList;

  return offered.filter(kind => kind === 'OTHER' || !returnDatePending);
}

export type TravelRequestIssueCode =
  | 'TRAVEL_REQUEST_INCOMPLETE'
  | 'TRAVEL_REQUEST_DATES_INVALID'
  | 'TRAVEL_REQUEST_DEPARTURE_PAST'
  | 'TRAVEL_REQUEST_EVENT_DATES_INVALID'
  | 'TRAVEL_REQUEST_EVENT_DATES_OUTSIDE_TRIP'
  | 'TRAVEL_REQUEST_FIELD_REQUIRED'
  | 'TRAVEL_REQUEST_FIELD_NOT_ALLOWED'
  | 'TRAVEL_REQUEST_ABSENCE_INVALID'
  | 'TRAVEL_REQUEST_EXPENSES_INVALID'
  | 'TRAVEL_REQUEST_FILES_MISSING'
  | 'TRAVEL_ERASMUS_CERTIFICATE_PENDING'
  | 'TRAVEL_EARLIER_RETURN_PENDING'
  | 'TRAVEL_REQUEST_LEAVE_DAYS_EXCEED_WORKING_DAYS'
  | 'TRAVEL_REQUEST_ABSENCE_MOTIVATION_NOT_ALLOWED'
  | 'TRAVEL_REQUEST_DAY_CAP_EXCEEDED'
  | 'TRAVEL_REQUEST_PARALLEL_TRIP';

export type TravelRequestIssueRule =
  | 'fieldRequired'
  | 'fieldNotAllowed'
  | 'returnBeforeDeparture'
  | 'departurePast'
  | 'eventEndBeforeStart'
  | 'eventOutsideTrip'
  | 'leaveDaysAtLeastOne'
  | 'capDaysAboveUsable'
  | 'leaveDaysExceedWorkingDays'
  | 'motivationNotAllowed'
  | 'dayCapExceeded'
  | 'expenseAmountInvalid'
  | 'expenseEurAmountRequired'
  | 'expenseErasmusFundsOnly'
  | 'expenseMobilitySupportActivity'
  | 'expenseMobilitySupportDuplicate'
  | 'expenseSourceRepeated'
  | 'fileMissing'
  | 'erasmusCertificatePending'
  | 'earlierReturnPending'
  | 'parallelTrip';

export type TravelBlockingRule = Extract<TravelRequestIssueRule, 'erasmusCertificatePending' | 'earlierReturnPending'>;

export interface TravelRequestIssue {
  code: TravelRequestIssueCode;
  rule: TravelRequestIssueRule;
  field: string | null;
  expenseIndex: number | null;
  fileKind: TravelFileKind | null;
  blockingRequest: TravelOverlapRequest | null;
  values?: Record<string, number>;
}

function isBlank(value: string | null | undefined) {
  return value == null || value.trim() === '';
}

function issue(
  code: TravelRequestIssueCode,
  rule: TravelRequestIssueRule,
  field: string | null,
  extra: Partial<Pick<TravelRequestIssue, 'expenseIndex' | 'fileKind' | 'blockingRequest' | 'values'>> = {},
): TravelRequestIssue {
  return { code, rule, field, expenseIndex: null, fileKind: null, blockingRequest: null, ...extra };
}

// One entry per year over a cap, reporting the with-paper pool when it overflows and the professional development pool otherwise.
export function exceededDayBalanceYears(preview: TravelDayBalancePreview): Record<string, number>[] {
  return preview.years
    .filter(year => year.exceedsWithPaper || year.exceedsProfessionalDevelopment)
    .map(year => {
      const pd = !year.exceedsWithPaper;
      const used = pd ? year.professionalDevelopmentUsed : year.withPaperUsed;
      const cap = pd ? year.professionalDevelopmentCap : year.withPaperCap;
      const requested = pd ? year.thisRequestProfessionalDevelopmentDays : year.thisRequestPaidDays;
      return { year: year.year, used, cap, requested, remaining: Math.max(cap - used, 0) };
    });
}

// The beyond-cap motivation needs one leave day unless the trip holds no working day to take leave on.
export function travelMinimumLeaveDays(preview: TravelDayBalancePreview | null | undefined): number {
  if (preview?.workingDays === 0) return 0;
  return Math.max(preview?.minimumLeaveDays ?? 0, 1);
}

// Uncovered days are reported against the usable cap days, so on its own this motivation only owes its first leave day.
function owesFirstLeaveDay(preview: TravelDayBalancePreview | null | undefined, leaveDays: number): boolean {
  return preview?.workingDays !== 0 && leaveDays < 1;
}

export type TravelDaySumProblem = Extract<
  TravelRequestIssueRule,
  'leaveDaysExceedWorkingDays' | 'capDaysAboveUsable' | 'leaveDaysAtLeastOne'
>;

export interface TravelDaySum {
  workingDays: number;
  capDays: number;
  vacationDays: number;
  unpaidDays: number;
  capUsableDays: number | null;
  missingLeaveDays: number;
  problem: TravelDaySumProblem | null;
}

// The working days no leave covers are charged to the yearly cap, under every motivation that takes leave.
export function travelDaySum(
  totals: Pick<TravelDayBalancePreview, 'workingDays' | 'capUsableDays'>,
  motivation: AbsenceMotivation | null | undefined,
  vacationDays: number | null | undefined,
  unpaidDays: number | null | undefined,
  capDaysBlockSubmit = true,
): TravelDaySum | null {
  if (!absenceMotivationTakesLeave(motivation)) return null;

  const { workingDays } = totals;
  const capUsableDays = totals.capUsableDays ?? null;
  const vacation = vacationDays ?? 0;
  const unpaid = unpaidDays ?? 0;
  const leaveDays = vacation + unpaid;
  const capDays = Math.max(workingDays - leaveDays, 0);
  const missingLeaveDays = capUsableDays == null ? 0 : Math.max(capDays - capUsableDays, 0);

  const noLeaveBeyondCap = motivation === 'BEYOND_CAP_ON_LEAVE' && workingDays > 0 && leaveDays < 1;
  let problem: TravelDaySumProblem | null = null;
  if (leaveDays > workingDays) problem = 'leaveDaysExceedWorkingDays';
  // A missing first leave day always blocks, so it outranks uncovered days that would only flag the request.
  else if (missingLeaveDays > 0 && (capDaysBlockSubmit || !noLeaveBeyondCap)) problem = 'capDaysAboveUsable';
  else if (noLeaveBeyondCap) problem = 'leaveDaysAtLeastOne';

  return { workingDays, capDays, vacationDays: vacation, unpaidDays: unpaid, capUsableDays, missingLeaveDays, problem };
}

// Under WARN the backend only flags uncovered days over the cap, except beyond the cap on a scientific event without a paper.
export function travelCapDaysBlockSubmit(
  enforcement: TravelEnforcement | undefined,
  motivation: AbsenceMotivation | null | undefined,
  activityType: TravelActivityType,
  presentsPaper: boolean | null | undefined,
): boolean {
  const paperless = activityType === 'SCIENTIFIC_EVENT' && presentsPaper !== true;
  return enforcement === 'BLOCK' || (motivation === 'BEYOND_CAP_ON_LEAVE' && paperless);
}

// Mirrors the backend submit validator family by family, in its evaluation order, but lists every failing rule instead of stopping at the first family.
export function travelRequestIssues(
  request: TravelRequest,
  documentKinds: TravelDocumentKinds | undefined,
  preview?: TravelDayBalancePreview | null,
): TravelRequestIssue[] {
  const { activityType, absence, expenses, files } = request;
  const isErasmus = activityType === 'ERASMUS';
  const isScientificEvent = activityType === 'SCIENTIFIC_EVENT';
  const issues: TravelRequestIssue[] = [];

  const required = (field: string) => issues.push(issue('TRAVEL_REQUEST_FIELD_REQUIRED', 'fieldRequired', field));
  const notAllowed = (field: string) =>
    issues.push(issue('TRAVEL_REQUEST_FIELD_NOT_ALLOWED', 'fieldNotAllowed', field));
  const absenceRequired = (field: string) =>
    issues.push(issue('TRAVEL_REQUEST_ABSENCE_INVALID', 'fieldRequired', field));

  if (request.departureDate == null) {
    issues.push(issue('TRAVEL_REQUEST_INCOMPLETE', 'fieldRequired', 'departureDate'));
  }
  if (request.returnDate == null) {
    issues.push(issue('TRAVEL_REQUEST_INCOMPLETE', 'fieldRequired', 'returnDate'));
  }
  if (request.departureDate != null && request.returnDate != null && request.returnDate < request.departureDate) {
    issues.push(issue('TRAVEL_REQUEST_DATES_INVALID', 'returnBeforeDeparture', 'returnDate'));
  }
  if (request.departureDate != null && request.departureDate < dayjs().format(DAYJS_SERVER_DATE_FORMAT)) {
    issues.push(issue('TRAVEL_REQUEST_DEPARTURE_PAST', 'departurePast', 'departureDate'));
  }
  const { eventStartDate, eventEndDate } = request;
  if (eventStartDate == null) {
    issues.push(issue('TRAVEL_REQUEST_INCOMPLETE', 'fieldRequired', 'eventStartDate'));
  }
  if (eventEndDate == null) {
    issues.push(issue('TRAVEL_REQUEST_INCOMPLETE', 'fieldRequired', 'eventEndDate'));
  }
  if (eventStartDate != null && eventEndDate != null && eventEndDate < eventStartDate) {
    issues.push(issue('TRAVEL_REQUEST_EVENT_DATES_INVALID', 'eventEndBeforeStart', 'eventEndDate'));
  }
  if (eventStartDate != null && request.departureDate != null && eventStartDate < request.departureDate) {
    issues.push(issue('TRAVEL_REQUEST_EVENT_DATES_OUTSIDE_TRIP', 'eventOutsideTrip', 'eventStartDate'));
  }
  if (eventEndDate != null && request.returnDate != null && eventEndDate > request.returnDate) {
    issues.push(issue('TRAVEL_REQUEST_EVENT_DATES_OUTSIDE_TRIP', 'eventOutsideTrip', 'eventEndDate'));
  }

  if (isBlank(request.city)) required('city');
  if (request.country == null) required('country');
  if (isErasmus) {
    if (request.erasmusType == null) required('erasmusType');
  } else if (isBlank(request.name)) {
    required(`nameByType.${activityType}`);
  }
  if (isBlank(request.destinationInstitution)) required(travelDestinationInstitutionField(activityType));
  if (isScientificEvent && request.presentsPaper === true && isBlank(request.paperTitle)) required('paperTitle');
  if (absence?.teachingCoverageMode == null) required('teachingCoverageMode');
  if (absence?.teachingCoverageMode === 'COLLEAGUE_SUBSTITUTION' && isBlank(absence.substitutePersonName)) {
    required('substitutePersonName');
  }

  if (!isErasmus && request.erasmusType != null) notAllowed('erasmusType');
  if (!isScientificEvent) {
    if (request.presentsPaper === true) notAllowed('presentsPaper');
    if (!isBlank(request.paperTitle)) notAllowed('paperTitle');
  }
  const vacationDays = absence?.vacationDays ?? 0;
  const unpaidDays = absence?.unpaidDays ?? 0;
  const onLeave = !isErasmus && absence?.absenceMotivation === 'BEYOND_CAP_ON_LEAVE';
  if (isErasmus && absence?.absenceMotivation != null) notAllowed('absenceMotivation');
  if (isErasmus || !absenceMotivationTakesLeave(absence?.absenceMotivation)) {
    if (vacationDays > 0) notAllowed('vacationDays');
    if (unpaidDays > 0) notAllowed('unpaidDays');
  }
  if (isErasmus && !isBlank(absence?.ownProgramsMention)) notAllowed('ownProgramsMention');
  const capDaysBlock = travelCapDaysBlockSubmit(
    preview?.enforcement.dayCap,
    absence?.absenceMotivation,
    activityType,
    request.presentsPaper,
  );
  const daySum =
    isErasmus || preview == null
      ? null
      : travelDaySum(preview, absence?.absenceMotivation, vacationDays, unpaidDays, capDaysBlock);
  // No leave fixes a motivation the activity type refuses, which is reported on its own.
  const motivationRefused =
    absence?.absenceMotivation != null && preview?.allowedMotivations.includes(absence.absenceMotivation) === false;
  const capDaysAboveUsable = capDaysBlock && !motivationRefused && daySum?.problem === 'capDaysAboveUsable';

  if (!isErasmus) {
    const motivation = absence?.absenceMotivation ?? null;
    if (motivation == null) absenceRequired('absenceMotivation');
    if (motivation != null && preview != null && !preview.allowedMotivations.includes(motivation)) {
      issues.push(issue('TRAVEL_REQUEST_ABSENCE_MOTIVATION_NOT_ALLOWED', 'motivationNotAllowed', 'absenceMotivation'));
    }
    if (onLeave && !capDaysAboveUsable && owesFirstLeaveDay(preview, vacationDays + unpaidDays)) {
      issues.push(issue('TRAVEL_REQUEST_ABSENCE_INVALID', 'leaveDaysAtLeastOne', null));
    }
    if (motivation === 'OWN_PROGRAMS' && isBlank(absence?.ownProgramsMention)) absenceRequired('ownProgramsMention');
  }

  // Under WARN the backend accepts both and flags the request, so only BLOCK turns them into issues.
  if (preview?.enforcement.leaveDays === 'BLOCK' && preview.leaveDaysExceedWorkingDays) {
    issues.push(
      issue('TRAVEL_REQUEST_LEAVE_DAYS_EXCEED_WORKING_DAYS', 'leaveDaysExceedWorkingDays', null, {
        values: { leaveDays: preview.leaveDays, workingDays: preview.workingDays },
      }),
    );
  }
  if (daySum != null && capDaysAboveUsable) {
    const values = { usable: daySum.capUsableDays ?? 0, missing: daySum.missingLeaveDays };
    issues.push(issue('TRAVEL_REQUEST_DAY_CAP_EXCEEDED', 'capDaysAboveUsable', null, { values }));
  } else if (!motivationRefused && preview?.enforcement.dayCap === 'BLOCK') {
    exceededDayBalanceYears(preview).forEach(values =>
      issues.push(issue('TRAVEL_REQUEST_DAY_CAP_EXCEEDED', 'dayCapExceeded', null, { values })),
    );
  }

  const expenseKeys = expenses.map(travelExpenseKey);
  expenses.forEach((expense, expenseIndex) => {
    const isMobilitySupport = expense.expenseType === 'UMFST_MOBILITY_SUPPORT';
    // A second mobility support line is already reported on its type.
    if (!isMobilitySupport && expenseKeys.indexOf(expenseKeys[expenseIndex]) !== expenseIndex) {
      issues.push(issue('TRAVEL_REQUEST_EXPENSES_INVALID', 'expenseSourceRepeated', 'fundingSource', { expenseIndex }));
    }
    if (isMobilitySupport) {
      if (activityType !== 'PROFESSIONAL_DEVELOPMENT') {
        issues.push(
          issue('TRAVEL_REQUEST_EXPENSES_INVALID', 'expenseMobilitySupportActivity', 'expenseType', { expenseIndex }),
        );
      }
      if (expenses.findIndex(other => other.expenseType === 'UMFST_MOBILITY_SUPPORT') !== expenseIndex) {
        issues.push(
          issue('TRAVEL_REQUEST_EXPENSES_INVALID', 'expenseMobilitySupportDuplicate', 'expenseType', { expenseIndex }),
        );
      }
    } else if (expense.requestedAmount == null || expense.requestedAmount < 0) {
      issues.push(
        issue('TRAVEL_REQUEST_EXPENSES_INVALID', 'expenseAmountInvalid', 'requestedAmount', { expenseIndex }),
      );
    }
    // The support line is a yes/no tick that carries no amount.
    if (!isMobilitySupport && expense.currency === 'RON') {
      if (expense.eurAmount == null || expense.eurAmount < 0) {
        issues.push(
          issue('TRAVEL_REQUEST_EXPENSES_INVALID', 'expenseEurAmountRequired', 'eurAmount', { expenseIndex }),
        );
      }
      if (
        expense.fundingSource === 'PROJECT_FUNDS' &&
        expense.projectAmount != null &&
        (expense.projectEurAmount == null || expense.projectEurAmount < 0)
      ) {
        issues.push(
          issue('TRAVEL_REQUEST_EXPENSES_INVALID', 'expenseEurAmountRequired', 'projectEurAmount', { expenseIndex }),
        );
      }
    }
    if (isErasmus && expense.fundingSource !== 'ERASMUS_FUNDS') {
      issues.push(
        issue('TRAVEL_REQUEST_EXPENSES_INVALID', 'expenseErasmusFundsOnly', 'fundingSource', { expenseIndex }),
      );
    }
  });

  const uploadedKinds = new Set(files.map(file => file.kind));
  requiredTravelSubmissionKinds(request, documentKinds)
    .filter(kind => !uploadedKinds.has(kind))
    .forEach(fileKind => issues.push(issue('TRAVEL_REQUEST_FILES_MISSING', 'fileMissing', null, { fileKind })));

  request.unfinishedErasmusRequests.forEach(blockingRequest =>
    issues.push(issue('TRAVEL_ERASMUS_CERTIFICATE_PENDING', 'erasmusCertificatePending', null, { blockingRequest })),
  );
  if (!isErasmus) {
    (request.unfinishedReturnRequests ?? []).forEach(blockingRequest =>
      issues.push(issue('TRAVEL_EARLIER_RETURN_PENDING', 'earlierReturnPending', null, { blockingRequest })),
    );
  }
  if (request.parallelEnforcement === 'BLOCK' && request.parallelRequests > 0) {
    issues.push(
      issue('TRAVEL_REQUEST_PARALLEL_TRIP', 'parallelTrip', null, { values: { count: request.parallelRequests } }),
    );
  }

  return issues;
}
